// <copyright company="Microsoft">Copyright (c) Microsoft. All rights reserved.</copyright>

import React, { useId, useLayoutEffect, useRef, useState } from 'react';
import { Button, Text, makeStyles, mergeClasses, shorthands, tokens } from '@fluentui/react-components';
import { DocumentRegular } from '@fluentui/react-icons';
import { RelatedCfdi } from './cfdiLookupService';
import CfdiUuidActions from './CfdiUuidActions';

const useStyles = makeStyles({
    controls: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '12px' },
    muted: { color: tokens.colorNeutralForeground3 },
    switch: {
        display: 'flex', gap: '4px', padding: '4px',
        ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
        ...shorthands.borderRadius(tokens.borderRadiusLarge),
        backgroundColor: tokens.colorNeutralBackground1,
        '@media (max-width: 480px)': { width: '100%' },
    },
    viewButton: { '@media (max-width: 480px)': { flex: 1 } },
    selectedView: { backgroundColor: tokens.colorPaletteBlueBackground2, color: tokens.colorPaletteBlueForeground2 },
    board: {
        display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.08fr) minmax(0, 1fr)',
        alignItems: 'center', columnGap: '40px', position: 'relative', minWidth: 0, ...shorthands.padding('20px', '0'),
        '@media (max-width: 1100px)': { gridTemplateColumns: 'minmax(0, 1fr)', gap: '32px', ...shorthands.padding('8px', '24px') },
        '@media (max-width: 480px)': { paddingLeft: '20px', paddingRight: '20px' },
    },
    wires: {
        position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none',
        '& path': { fill: 'none', stroke: tokens.colorNeutralStrokeAccessible, strokeWidth: '1.5' },
    },
    group: { position: 'relative', minWidth: 0 },
    groupTitle: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', margin: 0, marginBottom: '12px',
        fontSize: tokens.fontSizeBase300, fontWeight: tokens.fontWeightSemibold, lineHeight: tokens.lineHeightBase300,
    },
    count: {
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '24px', height: '24px',
        ...shorthands.padding('0', '6px'), ...shorthands.borderRadius(tokens.borderRadiusLarge),
        backgroundColor: tokens.colorNeutralBackground3, color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200,
    },
    nodes: { display: 'flex', flexDirection: 'column', gap: '12px', margin: 0, padding: 0, listStyleType: 'none' },
    node: {
        backgroundColor: tokens.colorNeutralBackground1, ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
        ...shorthands.borderRadius(tokens.borderRadiusXLarge), padding: '16px', minWidth: 0, position: 'relative',
        '@media (max-width: 480px)': { padding: '12px' },
    },
    caption: { display: 'flex', alignItems: 'center', gap: '8px', color: tokens.colorNeutralForeground3, marginBottom: '8px' },
    focusTitle: { color: tokens.colorPaletteBlueForeground2 },
    focus: { ...shorthands.border('2px', 'solid', tokens.colorPaletteBlueBorderActive), ...shorthands.padding('20px', '16px') },
    focusBadge: {
        display: 'inline-block', ...shorthands.padding('4px', '8px'), marginBottom: '12px',
        ...shorthands.borderRadius(tokens.borderRadiusMedium), backgroundColor: tokens.colorPaletteBlueBackground2,
        color: tokens.colorPaletteBlueForeground2, fontSize: tokens.fontSizeBase100, fontWeight: tokens.fontWeightSemibold,
        letterSpacing: '0.6px',
    },
    focusNote: { display: 'block', marginTop: '12px', color: tokens.colorNeutralForeground3 },
    empty: {
        padding: '20px', ...shorthands.border('1px', 'dashed', tokens.colorNeutralStroke2),
        ...shorthands.borderRadius(tokens.borderRadiusXLarge), backgroundColor: tokens.colorNeutralBackground3,
        color: tokens.colorNeutralForeground3,
    },
    expand: { marginTop: '12px' },
    legend: { display: 'block', marginTop: '12px', color: tokens.colorNeutralForeground3 },
});

export default function CfdiRelationships({ uuid, parents, children, busy, onConsult, list }: {
    uuid: string; parents: RelatedCfdi[]; children: RelatedCfdi[]; busy: boolean;
    onConsult: (uuid: string) => void; list: React.ReactNode;
}) {
    const styles = useStyles();
    const id = useId();
    const [view, setView] = useState<'map' | 'list'>('map');
    const [expanded, setExpanded] = useState({ parent: false, child: false });
    const board = useRef<HTMLDivElement>(null);
    const nodes = useRef(new Map<string, HTMLElement>());
    const [wires, setWires] = useState({ width: 0, height: 0, paths: [] as string[] });

    useLayoutEffect(() => {
        if (view !== 'map' || !board.current) return;
        const element = board.current;
        let frame = 0;
        function draw() {
            const bounds = element.getBoundingClientRect();
            const focus = nodes.current.get('focus')?.getBoundingClientRect();
            if (!focus || !bounds.width) return;
            const compact = window.matchMedia('(max-width: 1100px)').matches;
            const paths: string[] = [];
            for (const [key, node] of Array.from(nodes.current.entries())) {
                if (key === 'focus') continue;
                const rect = node.getBoundingClientRect();
                const parent = key.startsWith('parent');
                const y1 = rect.top + rect.height / 2 - bounds.top;
                const y2 = focus.top + focus.height / 2 - bounds.top;
                if (compact) {
                    const edge = parent ? 8 : bounds.width - 8;
                    const from = (parent ? rect.left : rect.right) - bounds.left;
                    const to = (parent ? focus.left : focus.right) - bounds.left;
                    paths.push(`M ${from} ${y1} H ${edge} V ${y2} H ${to}`);
                } else {
                    const from = (parent ? rect.right : rect.left) - bounds.left;
                    const to = (parent ? focus.left : focus.right) - bounds.left;
                    const middle = (from + to) / 2;
                    paths.push(`M ${from} ${y1} C ${middle} ${y1}, ${middle} ${y2}, ${to} ${y2}`);
                }
            }
            setWires({ width: bounds.width, height: bounds.height, paths });
        }
        function schedule() {
            window.cancelAnimationFrame(frame);
            frame = window.requestAnimationFrame(draw);
        }
        const observer = new ResizeObserver(schedule);
        observer.observe(element);
        for (const node of Array.from(nodes.current.values())) observer.observe(node);
        window.addEventListener('resize', schedule);
        schedule();
        return () => {
            observer.disconnect();
            window.removeEventListener('resize', schedule);
            window.cancelAnimationFrame(frame);
        };
    }, [uuid, parents, children, expanded, view]);

    function register(key: string, element: HTMLElement | null) {
        if (element) nodes.current.set(key, element);
        else nodes.current.delete(key);
    }

    function group(direction: 'parent' | 'child', documents: RelatedCfdi[]) {
        const visible = expanded[direction] ? documents : documents.slice(0, 3);
        return <section className={styles.group} aria-labelledby={`${id}-${direction}`}>
            <h3 id={`${id}-${direction}`} className={styles.groupTitle}>
                {direction === 'parent' ? 'Parent CFDIs' : 'Child CFDIs'}<span className={styles.count}>{documents.length}</span>
            </h3>
            <ul className={styles.nodes}>
                {visible.map((document, index) => <li key={`${document.uuid}-${index}`} className={styles.node} data-cfdi-node={direction}
                    ref={element => register(`${direction}-${index}`, element)}>
                    <Text size={200} className={styles.caption}><DocumentRegular aria-hidden="true" />
                        {direction === 'parent' ? 'Parent reference' : 'Child reference'}
                    </Text>
                    <CfdiUuidActions document={document} busy={busy} onConsult={onConsult} />
                </li>)}
            </ul>
            {documents.length === 0 && <div className={styles.empty}>No {direction} CFDIs returned for this CFDI.</div>}
            {documents.length > 3 && <Button appearance="transparent" className={styles.expand}
                aria-expanded={expanded[direction]} onClick={() => setExpanded(value => ({ ...value, [direction]: !value[direction] }))}>
                {expanded[direction] ? 'Show fewer' : `Show all ${documents.length} ${direction} CFDIs`}
            </Button>}
        </section>;
    }

    return <div>
        <div className={styles.controls}>
            <Text size={200} className={styles.muted}>One-hop relationships around the consulted UUID.</Text>
            <div className={styles.switch} role="group" aria-label="Relationship presentation">
                {(['map', 'list'] as const).map(value => <Button key={value} appearance="subtle"
                    className={mergeClasses(styles.viewButton, view === value && styles.selectedView)}
                    aria-pressed={view === value} onClick={() => setView(value)}>
                    {value === 'map' ? 'Relationship map' : 'Compact list'}
                </Button>)}
            </div>
        </div>
        {view === 'list' ? list : <>
            <div className={styles.board} ref={board} role="group" aria-label="CFDI relationship map">
                {wires.width > 0 && <svg className={styles.wires} data-cfdi-connectors aria-hidden="true" focusable="false"
                    viewBox={`0 0 ${wires.width} ${wires.height}`} preserveAspectRatio="none">
                    {wires.paths.map((d, index) => <path key={index} d={d} />)}
                </svg>}
                {group('parent', parents)}
                <div className={styles.group}>
                    <h3 className={mergeClasses(styles.groupTitle, styles.focusTitle)}>Consulted CFDI</h3>
                    <div className={mergeClasses(styles.node, styles.focus)} data-cfdi-node="focus"
                        ref={element => register('focus', element)}>
                        <span className={styles.focusBadge}>CURRENT LOOKUP</span>
                        <CfdiUuidActions document={{ uuid, canLookup: true }} />
                        <Text size={200} className={styles.focusNote}>Relationships are relative to this UUID.</Text>
                    </div>
                </div>
                {group('child', children)}
            </div>
            <Text size={200} className={styles.legend}>Lines indicate reported relationships, not cancellation order.
                Document types have not been queried.</Text>
        </>}
    </div>;
}
